import { Fragment, CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import moduleStyles from './PipelineBuilder.module.scss';
import Tooltip from '../../../components/tooltip/Tooltip';

export type TpelkPortLabelDefinition = {
  text: string;
  width: number;
  height: number;
};

type TpelkPortLabelResult = {
  x: number;
  y: number;
} & TpelkPortLabelDefinition;

export type TPelkPortDefinition = {
  id: string;
  width: number;
  height: number;
  properties: {
    outputId: string;
    outputName: string;
    side: 'EAST' | 'WEST';
    borderOffset: number; // Offset from the component border
  };
  labels: TpelkPortLabelDefinition[];
};

export type TPelkPort = TPelkPortDefinition & {
  labels: TpelkPortLabelResult[];
  x: number;
  y: number;
};

export type TpOutputPortProps = {
  port: TPelkPort;
  onSelect: (outputId: string) => void; // Function to handle click events on the port
  isConnected: boolean; // Is there an edge connected to this port?
  selectionIndex: number; // Index of the port in the selection
  className?: string; // Additional class name for styling
  styles?: CSSProperties;
  margin?: number; // Extra margin between ports and edges
  showSelectionNumber?: boolean; // Show the selection number on the port
};

/**
 * Component representing a port of a component in the ETL pipeline builder.
 * This component is used to render output ports for components in the pipeline.
 */
export function OutputPort({
  port,
  onSelect,
  selectionIndex,
  isConnected,
  className = '',
  styles = {},
  margin = 0,
  showSelectionNumber = true,
}: TpOutputPortProps) {
  const outputId = port.properties.outputId;
  const outputName = port.properties.outputName;
  const selected = selectionIndex >= 0;
  const portX = port.x - (port.properties.side === 'EAST' ? 2 * margin : 0);
  const portLabel = port.labels.length > 0 ? port.labels[0] : null;
  return (
    <Fragment>
      <Tooltip
        className={`${moduleStyles.compPortOut} ${className}`}
        style={{
          left: `${portX}px`,
          top: `${port.y - margin}px`,
          width: `${port.width}px`,
          height: `${port.height}px`,
          ...styles,
        }}
        onClick={(ev) => {
          ev.stopPropagation();
          onSelect(outputId);
        }}
        content={outputName}
        direction="right"
      >
        <span className={`fa-layers ${moduleStyles.portIconWrapper}`}>
          {!isConnected && !selected && (
            <FontAwesomeIcon icon={['fas', 'arrow-right']} className={moduleStyles.portArrowIcon} />
          )}
          <FontAwesomeIcon
            icon={['fas', 'circle-check']}
            className={selected ? moduleStyles.portSelectedIcon : moduleStyles.portUnSelectedIcon}
          />
        </span>
      </Tooltip>
      {portLabel && selected && showSelectionNumber && (
        <div
          className={moduleStyles.selectionNumberIcon}
          style={{
            left: `${portX + portLabel.x}px`,
            top: `${port.y - margin + portLabel.y}px`,
            width: `${portLabel.height}px`,
            height: `${portLabel.height}px`,
            lineHeight: `${portLabel.height}px`,
          }}
        >
          {selectionIndex + 1}
        </div>
      )}
    </Fragment>
  );
}
